import {
  DateValue,
  ZonedDateTime,
  getLocalTimeZone,
  parseZonedDateTime,
} from '@internationalized/date';

const ISO_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}(?::?\d{2})?)?(?:\[[A-Za-z_\/]+\])?$/;

export const parseApiDateToDateValue = (
  apiDate?: string | null,
  fallbackTimeZone: string = getLocalTimeZone(),
): ZonedDateTime | null => {
  if (!apiDate || typeof apiDate !== 'string') {
    return null;
  }

  let normalized = apiDate.trim();
  normalized = normalized.replace(/\s+/g, ' ');
  normalized = normalized.replace(/\.(\d{3})\d*/, '.$1');
  normalized = normalized.replace(/\[(.+?)\]/, '');

  if (!ISO_REGEX.test(normalized)) {
    const asDate = new Date(normalized);
    if (Number.isNaN(asDate.getTime())) {
      return null;
    }

    return zonedFromDate(asDate, fallbackTimeZone);
  }

  try {
    return parseZonedDateTime(normalized);
  } catch {
    const asDate = new Date(normalized);
    if (Number.isNaN(asDate.getTime())) {
      return null;
    }
    return zonedFromDate(asDate, fallbackTimeZone);
  }
};

export const dateValueToIsoString = (
  value: DateValue | null | undefined,
  timeZone: string = getLocalTimeZone(),
): string | null => {
  if (!value) {
    return null;
  }

  const jsDate = value.toDate(timeZone);
  return jsDate.toISOString();
};

export const dateValueToTimeString = (
  value: DateValue | null | undefined,
  timeZone: string = getLocalTimeZone(),
): string | null => {
  if (!value) {
    return null;
  }

  const jsDate = value.toDate(timeZone);

  return jsDate
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone,
    })
    .trim();
};

export const dateValueToDateAndTimeFields = (
  value: DateValue | null | undefined,
  timeZone: string = getLocalTimeZone(),
): { iso: string; time: string } | null => {
  const iso = dateValueToIsoString(value, timeZone);
  const time = dateValueToTimeString(value, timeZone);

  if (!iso || !time) {
    return null;
  }

  return {
    iso,
    time,
  };
};

const zonedFromDate = (date: Date, timeZone: string): ZonedDateTime => {
  return new ZonedDateTime(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
    timeZone,
    date.getTimezoneOffset() * -60 * 1000,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds(),
  );
};
